/**
 * Contact lookup, MOBILE — the second and last mobile reduction.
 *
 * WHY THIS FLOW. A rep standing in a corridor needs to answer "what was that
 * doctor's number" and then call them. That is a search box, a list, and two
 * buttons — and on a phone it is a better experience than the desktop table
 * could ever be, which is the test for whether a mobile variant is worth
 * having at all.
 *
 * It uses the SAME `searchContacts` query the workbench uses, with the same
 * filter shape. No mobile-only endpoint, no mobile-only field: a second query
 * would be a second definition of what "matching" means, and Persian digit
 * folding is exactly the kind of thing that would then be right in one place
 * and wrong in the other.
 *
 * **The deep links are the point.** `tel:` and `https://wa.me/…` hand the
 * number to the phone's own dialer and WhatsApp app. MeshHub sends nothing:
 * this is not the messaging service, no message is recorded, and no opt-out is
 * consulted — because the rep is placing a call, not the system sending a
 * broadcast. Conflating the two would put an unlogged, unsuppressed send into
 * the one surface that has no suppression chain.
 */
import { EmptyState, Spinner } from '@meshhub/design-tokens/ui';
import { useQuery } from '@apollo/client';
import { MessageCircle, Phone, Plus, Search } from 'lucide-react';
import React, { useDeferredValue, useState } from 'react';
import { QuickAddSheet } from '../components/QuickAdd.mobile.jsx';
import { toPersianDigits } from '../lib/format.js';
import { SEARCH_CONTACTS_QUERY } from '../lib/queries.js';
import { S } from '../lib/strings.js';

/**
 * A number for `wa.me`, which wants digits only — no `+`, no separators.
 *
 * Returns null rather than a broken link when there is no number: a WhatsApp
 * button that opens an empty chat is worse than no button.
 */
function whatsappHref(e164) {
  if (!e164) return null;
  const digits = String(e164).replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : null;
}

export function ContactLookupMobile() {
  const [term, setTerm] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  // Deferred rather than debounced: the workbench debounces its query
  // variables, and on a phone the list is short enough that React's own
  // deferral gives a smoother keystroke without a second timing mechanism.
  const deferred = useDeferredValue(term);

  const { data, loading } = useQuery(SEARCH_CONTACTS_QUERY, {
    variables: {
      filter: deferred.trim() ? { term: deferred.trim() } : {},
      page: 1,
      pageSize: 25,
      withFacets: false,
    },
    skip: !deferred.trim(),
  });

  const items = data?.searchContacts?.items ?? [];

  return (
    <div dir="rtl" className="flex h-screen flex-col bg-bg">
      <header className="flex items-center gap-2 border-b border-border p-3">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute inset-y-0 end-3 my-auto text-text-muted"
            aria-hidden="true"
          />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={S.contacts.searchPlaceholder}
            aria-label={S.contacts.searchLabel}
            // `search` rather than `text`: iOS gives a clear button and a
            // "Search" key on the keyboard for free.
            type="search"
            autoFocus
            className="h-control w-full rounded border border-border bg-surface px-3 pe-9 text-base
                       outline-none focus:border-primary"
          />
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          aria-label={S.quickAdd.open}
          className="h-control w-control rounded bg-primary text-primary-contrast"
        >
          <Plus size={18} aria-hidden="true" />
        </button>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        {loading && <Spinner />}
        {!loading && deferred.trim() && items.length === 0 && (
          <EmptyState title={S.app.notFound} hint={S.contacts.emptyHint} />
        )}
        {!deferred.trim() && <EmptyState title={S.contacts.searchPlaceholder} />}

        <ul className="flex flex-col divide-y divide-border">
          {items.map((contact) => {
            const wa = whatsappHref(contact.primaryPhoneE164);
            return (
              <li key={contact.id} className="flex items-center gap-2 p-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base text-text">{contact.displayName}</p>
                  {contact.primaryPhone && (
                    <p className="text-sm text-text-muted" dir="ltr">
                      {toPersianDigits(contact.primaryPhone)}
                    </p>
                  )}
                </div>
                {contact.primaryPhoneE164 && (
                  <a
                    href={`tel:${contact.primaryPhoneE164}`}
                    aria-label={`${S.mobile.call} ${contact.displayName}`}
                    className="flex h-control w-control items-center justify-center rounded
                               border border-border text-primary"
                  >
                    <Phone size={18} aria-hidden="true" />
                  </a>
                )}
                {wa && (
                  <a
                    href={wa}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${S.mobile.whatsapp} ${contact.displayName}`}
                    className="flex h-control w-control items-center justify-center rounded
                               border border-border text-success"
                  >
                    <MessageCircle size={18} aria-hidden="true" />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </main>

      <QuickAddSheet open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
